import { faBars, faChevronDown, faChevronUp, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faCoins } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LoginMenu from 'components/ApiAuthorization/LoginMenu';
import Flex from 'components/ui/Flex';
import { Search } from 'components/ui/Search';
import SlideIn from 'components/ui/SlideIn';
import useAppSelector from 'hooks/useAppSelector';
import useBreakPoints from 'hooks/useBreakPoints';
import useOnClickOutside from 'hooks/useCloseOnClickOutside';
import useHeaderScroll from 'hooks/useHeaderScroll';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { selectRemainingBudget, selectShowBudgetToUser } from 'store/reducers/cartSlice';
import styled from 'styled-components';
import CartButton from './CartButton';
import FilesNavigation from './FilesNavigation';
import HeaderIconButton from './HeaderIconButton';
import WithdrawalLink from './Withdrawal/WithdrawalLink';
import { selectCategoriesRaw } from 'store/reducers/assortmentSlice';
import useNavIconColor from 'hooks/useNavIconColor';

const MainNavigation = ({ loginLabel, logoutLabel }: { loginLabel: string; logoutLabel: string }) => {
    const { info, files, theme, unit } = useAppSelector(({ shop }) => ({
        ...shop,
        files: shop?.shop?.files ?? [],
        unit: shop?.shop?.selectedUnit,
    }));

    const remainingBudget = useAppSelector(selectRemainingBudget);
    const showBudgetToUser = useAppSelector(selectShowBudgetToUser);
    const categoriesRaw = useAppSelector(selectCategoriesRaw);
    const bp = useBreakPoints();
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const [showCategoryMenu, setShowCategoryMenu] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [categoriesOverflow, setCategoriesOverflow] = useState(false);
    const [categoriesOverflowInitialCheck, setCategoriesOverflowInitialCheck] = useState(false);
    const navListRef = useRef<HTMLDivElement>(null);
    const navVisible = useHeaderScroll();
    const mobileSearchRef = useOnClickOutside(() => setShowMobileSearch(false), showMobileSearch);
    const desktopSearchRef = useOnClickOutside(() => setSearchExpanded(false), searchExpanded);
    const iconColor = useNavIconColor();

    useEffect(() => {
        if (bp.md) {
            setShowMobileSearch(false);
        }
    }, [bp.md]);

    useEffect(() => {
        if (bp.mdBelow) return;
        const el = navListRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => {
            setCategoriesOverflow(el.scrollWidth > el.clientWidth);
            setCategoriesOverflowInitialCheck(true);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [categoriesRaw, bp.mdBelow]);

    return (
        <>
            <StyledNavigationWrapper column $visible={navVisible}>
                <StyledMainNavigation $bgColor={`${theme.main}`} $color={`${theme.mainTextColor}`}>
                    <StyledNavigation className="w-100 px-3 align-items-center">
                        <NavContent>
                            <Flex align="center" justify="start" $gap={40} style={{ flex: '1 1 0', minWidth: 0 }}>
                                {info?.logoType ? (
                                    <Link to="/">
                                        <StyledLogo alt={unit?.name ?? info?.name ?? ''} src={info?.logoType} />
                                    </Link>
                                ) : (
                                    <NavigationItem>
                                        <NavigationLink to="/">{unit?.name ?? info?.name}</NavigationLink>
                                    </NavigationItem>
                                )}
                                {!bp.mdBelow && categoriesRaw?.length > 0 && (
                                    <NavCategoryList
                                        ref={navListRef}
                                        $hidden={
                                            categoriesOverflow || showCategoryMenu || !categoriesOverflowInitialCheck
                                        }
                                    >
                                        {categoriesRaw.map((category) => {
                                            const hasChildren = Boolean(category.children?.length);
                                            const slug = `/${category.assortmentSlug ?? category.name}`;
                                            return (
                                                <NavCategoryItem key={category.code}>
                                                    <NavCategoryLink to={slug}>{category.name}</NavCategoryLink>
                                                    {hasChildren && (
                                                        <NavDropdown>
                                                            {(category.children ?? []).map((child) => (
                                                                <NavDropdownLink
                                                                    key={child.code}
                                                                    to={`${slug}/${child.assortmentSlug ?? child.name}`}
                                                                >
                                                                    {child.name}
                                                                </NavDropdownLink>
                                                            ))}
                                                        </NavDropdown>
                                                    )}
                                                </NavCategoryItem>
                                            );
                                        })}
                                    </NavCategoryList>
                                )}
                            </Flex>

                            <Flex
                                align="center"
                                justify="end"
                                $gap={20}
                                style={{ flexShrink: 0, marginLeft: 16 }}
                            >
                                {!bp.mdBelow && <WithdrawalLink navigation />}
                                {showBudgetToUser && bp.sm && (
                                    <Flex align="center" $gap={8}>
                                        <FontAwesomeIcon icon={faCoins} /> <div>{remainingBudget}</div>
                                    </Flex>
                                )}
                                <FilesNavigation files={files} direction="right" />
                                {bp.mdBelow && (
                                    <HeaderIconButton
                                        small
                                        iconColor={iconColor}
                                        icon={faSearch}
                                        onClick={() => setShowMobileSearch(true)}
                                    />
                                )}
                                {!bp.mdBelow && (
                                    <SearchContainer
                                        $expanded={searchExpanded}
                                        ref={desktopSearchRef}
                                        onClick={() => setSearchExpanded(!bp.lgBelow)}
                                    >
                                        <Search autoFocus={searchExpanded} />
                                    </SearchContainer>
                                )}
                                <Link to="/cart">
                                    <CartButton className="nav-link" />
                                </Link>
                                {(info?.securityType ?? 0) > 1 && (
                                    <StyledLoginMenu
                                        loginLabel={loginLabel}
                                        logoutLabel={logoutLabel}
                                        className="nav-link"
                                    />
                                )}
                                {((bp.mdBelow && info?.withdrawalEnabled) ||
                                    ((bp.mdBelow || (categoriesOverflowInitialCheck && categoriesOverflow)) &&
                                        categoriesRaw?.length > 0)) && (
                                        <HeaderIconButton
                                            small
                                            icon={faBars}
                                            iconColor={iconColor}
                                            onClick={() => setShowCategoryMenu(true)}
                                        />
                                    )}
                            </Flex>
                        </NavContent>

                        {bp.mdBelow && (
                            <MobileSearchOverlay $active={showMobileSearch} ref={mobileSearchRef}>
                                <MobileSearchContainer>
                                    <Search
                                        autoFocus={showMobileSearch}
                                        closeSearch={() => setShowMobileSearch(false)}
                                    />
                                </MobileSearchContainer>
                            </MobileSearchOverlay>
                        )}
                    </StyledNavigation>
                </StyledMainNavigation>
            </StyledNavigationWrapper>

            <CategorySlideInOverride>
                <SlideIn visible={showCategoryMenu} onClose={() => setShowCategoryMenu(false)}>
                    {{
                        content: (
                            <CategoryMenuContent>
                                {categoriesRaw?.map((category) => {
                                    const slug = `/${category.assortmentSlug ?? category.name}`;
                                    const hasChildren = Boolean(category.children?.length);
                                    const isExpanded = expandedCategory === category.code;
                                    return (
                                        <CategoryMenuSection key={category.code}>
                                            <CategoryMenuHeader>
                                                <CategoryMenuLink
                                                    to={slug}
                                                    onClick={() => {
                                                        setShowCategoryMenu(false);
                                                        setExpandedCategory(null);
                                                    }}
                                                >
                                                    {category.name}
                                                </CategoryMenuLink>
                                                {hasChildren && (
                                                    <CategoryExpandButton
                                                        onClick={() =>
                                                            setExpandedCategory(
                                                                isExpanded ? null : category.code ?? null,
                                                            )
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={isExpanded ? faChevronUp : faChevronDown}
                                                        />
                                                    </CategoryExpandButton>
                                                )}
                                            </CategoryMenuHeader>
                                            {hasChildren &&
                                                isExpanded &&
                                                category.children?.map((child) => (
                                                    <CategoryMenuSubLink
                                                        key={child.code}
                                                        to={`${slug}/${child.assortmentSlug ?? child.name}`}
                                                        onClick={() => {
                                                            setShowCategoryMenu(false);
                                                            setExpandedCategory(null);
                                                        }}
                                                    >
                                                        {child.name}
                                                    </CategoryMenuSubLink>
                                                ))}
                                        </CategoryMenuSection>
                                    );
                                })}
                                {bp.mdBelow && info?.withdrawalEnabled && (
                                    <CategoryMenuSection>
                                        <CategoryMenuHeader>
                                            <WithdrawalMenuLink onClick={() => setShowCategoryMenu(false)} />
                                        </CategoryMenuHeader>
                                    </CategoryMenuSection>
                                )}
                            </CategoryMenuContent>
                        ),
                    }}
                </SlideIn>
            </CategorySlideInOverride>
        </>
    );
};

const NavigationItem = styled.div`
    font-size: 18px;
    color: ${({ theme }) => theme.mainTextColor};
    cursor: default;
    padding-top: 2px;
    border-bottom: 2px solid transparent;
    &:hover {
        border-bottom-color: ${({ theme }) => theme.mainTextColor};
    }
`;

const NavigationLink = styled(Link)`
    font-size: 18px;
    color: ${({ theme }) => theme.mainTextColor};
    :hover,
    :focus,
    :visited {
        color: ${({ theme }) => theme.mainTextColor};
        text-decoration: none;
    }
`;

const StyledNavigationWrapper = styled(Flex)<{ $visible: boolean }>`
    max-width: ${({ theme }) => `${theme.breakpoints.xxl}px`};
    z-index: 999;
    position: fixed;
    width: 95%;
    left: 50%;
    top: 6%;
    transform: ${({ $visible }) => ($visible ? 'translate(-50%, -50%)' : 'translate(-50%, calc(-50% - 150px))')};
    transition: transform 0.3s ease;
    flex-wrap: nowrap;
    justify-content: flex-start;
`;

const StyledMainNavigation = styled.div<{ $bgColor: string; $color: string }>`
    ${({ $bgColor }) =>
        $bgColor && ($bgColor.includes('gradient') ? `background: ${$bgColor};` : `background-color: ${$bgColor};`)}
    ${({ $color }) => $color && `color: ${$color};`}
    height: 71px;
    width: 100%;
    border-radius: 30px;
`;

const StyledLogo = styled.img`
    height: 70px;
    max-width: 100%;
    width: auto;
    object-fit: contain;
    display: block;
    padding: 6px 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
        height: 50px;
    }
`;

const StyledNavigation = styled.div`
    max-width: ${({ theme }) => theme.breakpoints.xxl}px;
    height: 100%;
    margin: 0px auto;
    position: relative;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}px) {
        overflow: visible;
    }
`;

const NavContent = styled.div`
    display: flex;
    align-items: center;
    height: 100%;
    justify-content: space-between;
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
        margin: 0 40px;
    }
`;

const MobileSearchOverlay = styled.div<{ $active: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.main};
    display: flex;
    align-items: center;
    opacity: ${({ $active }) => ($active ? 1 : 0)};
    visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
    transition: opacity 0.2s ease, visibility 0.2s ease;
    z-index: 10;
    border-radius: 30px;
`;

const MobileSearchContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 16px; 
`;

const SearchContainer = styled.div<{ $expanded?: boolean }>`
    align-self: center;
    flex-shrink: 0;
    width: ${({ $expanded }) => ($expanded ? '450px' : '220px')};
    transition: width 0.3s ease;
    cursor: ${({ $expanded }) => ($expanded ? 'text' : 'pointer')};
`;

const StyledLoginMenu = styled(LoginMenu)`
    letter-spacing: 1.5px;
    text-transform: uppercase;
`;

const CategorySlideInOverride = styled.div`
    .slide-in-wrapper > div:first-child {
        justify-content: flex-end;
    }
    .slide-in-wrapper > div:first-child button svg {
        color: black;
    }
    ${({ theme }) => theme.media.lg} {
        .slide-in-wrapper {
            height: 100vh;
            top: 0;
        }
    }
`;

const CategoryMenuContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 4px;
`;

const CategoryMenuSection = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
`;

const CategoryMenuHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const CategoryExpandButton = styled.button`
    background: none;
    border: none;
    padding: 10px 8px;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.fillBlack};
    display: flex;
    align-items: center;
    flex-shrink: 0;
`;

const CategoryMenuLink = styled(Link)`
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.fillBlack} !important;
    text-decoration: none !important;
    padding: 10px 8px;
    flex: 1;
    &:hover {
        background: rgba(0, 0, 0, 0.04);
        border-radius: 4px;
    }
`;

const WithdrawalMenuLink = styled(WithdrawalLink)`
    flex: 1;
    padding: 10px 8px;
`;

const CategoryMenuSubLink = styled(Link)`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.text} !important;
    text-decoration: none !important;
    padding: 8px;
    &:hover {
        background: rgba(0, 0, 0, 0.04);
        border-radius: 4px;
    }
`;

const NavCategoryList = styled.div<{ $hidden?: boolean }>`
    display: flex;
    align-items: center;
    gap: 24px;
    height: 100%;
    flex: 1 1 0;
    min-width: 0;
    overflow: visible;
    clip-path: inset(0 0 -200px 0);
    opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
    pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
`;

const NavCategoryItem = styled.div`
    position: relative;
    height: 100%;
    display: flex;
    align-items: center;

    &:hover > div {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
    }
`;

const NavCategoryLink = styled(Link)`
    font-size: 14px;
    color: ${({ theme }) => theme.mainTextColor} !important;
    text-decoration: none !important;
    white-space: nowrap;
    padding: 4px 0;
    border-bottom: 2px solid transparent;
    transition: border-color 0.2s ease;

    &:hover {
        border-bottom-color: currentColor;
    }
`;

const NavDropdown = styled.div`
    position: absolute;
    top: 40px;
    left: 0;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.fillWhite} !important;
    padding: 8px 0;
    min-width: 180px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(-8px);
    transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;
    z-index: 10;
`;

const NavDropdownLink = styled(Link)`
    display: block;
    padding: 8px 16px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.fillBlack} !important;
    text-decoration: none !important;
    white-space: nowrap;
    &:hover {
        background: rgba(0, 0, 0, 0.1);
    }
`;

export default MainNavigation;
